import { useWalletObserver } from "@sundaeswap/wallet-lite";
import { FC, useMemo } from "react";
import { Address } from "@blaze-cardano/core";
import { AssetAmount } from "@sundaeswap/asset";
import { useGummiwormContext } from "@/context/GummiwormContext";
import { formatAda } from "@/lib/utils";
import { hashHexToU256 } from "@/lib/marketUtils";
import { useBrandName } from "@/hooks/useBrandName";

interface WalletBalanceProps {
  onDeposit: () => void;
  onWithdraw: () => void;
  depositDisabled?: boolean;
}

export const WalletBalance: FC<WalletBalanceProps> = ({ onDeposit, onWithdraw, depositDisabled }) => {
  const { activeWallet, mainAddress, adaBalance, errorSyncing } = useWalletObserver();
  const { accounts, accountsReady } = useGummiwormContext();
  const brandName = useBrandName();

  const myAccountId = useMemo(() => {
    if (!mainAddress) return null;
    const hash = Address.fromBech32(mainAddress).getProps().paymentPart?.hash;
    if (!hash) return null;
    return hashHexToU256(hash);
  }, [mainAddress]);

  const myAccount = useMemo(
    () => accounts.find((a) => a.accountId === myAccountId),
    [accounts, myAccountId],
  );

  const l2Balance = useMemo(() => {
    const ada = myAccount?.uncommittedValue.assets.find(
      (a) => a.asset.tag === "Ada",
    );
    const adaAsset = new AssetAmount(0n, { decimals: 6, assetId: "ada.lovelace" });
    if (!ada) return adaAsset;
    return adaAsset.withAmount(ada.value);
  }, [myAccount?.uncommittedValue]);

  if (!activeWallet || !mainAddress) {
    return null;
  }

  const hasL2Account = !!myAccount;
  const hasWalletBalance = adaBalance.amount > 0n;

  return (
    <div className="flex flex-col gap-4">
      {/* Balance card */}
      <div className="sr-card p-5">
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <span className="text-xs text-sr-neutral-500 uppercase tracking-wider">
              {hasL2Account ? `${brandName} Balance` : "Wallet Balance"}
            </span>
            <div className="flex items-center gap-1.5">
              <div className={`w-3 h-3 rounded-full ${errorSyncing ? "bg-sr-tangerine" : "bg-sr-mint"}`} />
              <span className="text-xs text-sr-neutral-500">
                {errorSyncing ? "Sync error" : "Connected"}
              </span>
            </div>
          </div>

          {hasL2Account ? (
            <>
              <div className="flex items-baseline gap-2">
                <span
                  className="text-3xl sm:text-4xl font-medium text-white tabular-nums"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  {formatAda(l2Balance)}
                </span>
                <span className="text-sm text-sr-neutral-500">ADA</span>
              </div>
              {hasWalletBalance && (
                <div className="flex items-baseline gap-1.5">
                  <span className="text-xs text-sr-neutral-500">Wallet:</span>
                  <span className="text-xs text-sr-neutral-400 tabular-nums">
                    {adaBalance.value.toString()} ADA
                  </span>
                </div>
              )}
            </>
          ) : !accountsReady ? (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full border-2 border-sr-neutral-500 border-t-transparent animate-spin" />
              <span className="text-sm text-sr-neutral-500">Loading balance...</span>
            </div>
          ) : errorSyncing || !hasWalletBalance ? (
            <div className="flex flex-col gap-1">
              <span className="text-lg font-medium text-white">
                Balance unavailable
              </span>
              <span className="text-xs text-sr-neutral-500">
                Your wallet couldn't report its balance. You can still deposit
                by entering an amount manually.
              </span>
            </div>
          ) : (
            <div className="flex items-baseline gap-2">
              <span
                className="text-3xl sm:text-4xl font-medium text-white tabular-nums"
                style={{ fontFamily: "var(--font-display)" }}
              >
                {adaBalance.value.toString()}
              </span>
              <span className="text-sm text-sr-neutral-500">ADA</span>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button className="sr-btn-primary flex-1" onClick={onDeposit} disabled={depositDisabled}>
          Deposit
        </button>
        {hasL2Account && (
          <button
            className="flex-1 rounded-full border border-white/[0.06] bg-white/[0.03] px-4 py-3 text-sm font-medium text-sr-neutral-300 transition-colors hover:bg-white/[0.05] hover:text-white"
            onClick={onWithdraw}
          >
            Withdraw
          </button>
        )}
      </div>
    </div>
  );
};
